import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import SpeakerProposalModal from './SpeakerProposalModal';

const {
  FiCalendar, FiChevronLeft, FiChevronRight,
  FiClock, FiZap, FiUsers, FiX,
} = FiIcons;

/** Safely extract the first word of a speaker name without crashing. */
const speakerFirst = (speaker) =>
  typeof speaker === 'string' && speaker.trim()
    ? speaker.trim().split(' ')[0]
    : 'Speaker';

/**
 * Build a map of { dayOfMonth → webinar } for the given month/year.
 * Guards against invalid date strings so the calendar grid never renders NaN.
 */
const buildWebinarDates = (webinars, year, month) => {
  if (!Array.isArray(webinars)) return {};
  return webinars.reduce((acc, w) => {
    if (!w?.date) return acc;
    const d = new Date(w.date);
    if (isNaN(d.getTime())) return acc;              // skip bad dates
    if (d.getMonth() === month && d.getFullYear() === year) {
      acc[d.getDate()] = w;
    }
    return acc;
  }, {});
};

const WebinarSidebarCalendar = ({ webinars = [], registeredIds = [] }) => {
  const navigate = useNavigate();
  const [currentMonth, setCurrentMonth]   = useState(new Date());
  const [selectedWebinar, setSelectedWebinar] = useState(null);
  const [isProposalOpen, setIsProposalOpen]   = useState(false);

  const year  = currentMonth.getFullYear();
  const month = currentMonth.getMonth();

  const daysInMonth    = new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = new Date(year, month, 1).getDay();

  const webinarDates = useMemo(
    () => buildWebinarDates(webinars, year, month),
    [webinars, year, month]
  );

  const prevMonth = () =>
    setCurrentMonth(new Date(year, month - 1, 1));
  const nextMonth = () =>
    setCurrentMonth(new Date(year, month + 1, 1));

  return (
    <div className="space-y-6">
      {/* Calendar Card */}
      <div className="bg-white rounded-[32px] border border-brand-dark p-6 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <h4 className="font-serif text-lg text-accent-navy">
            {currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
          </h4>
          <div className="flex gap-1">
            <button
              onClick={prevMonth}
              className="p-1.5 hover:bg-brand-light rounded-full text-text-muted transition-colors"
            >
              <SafeIcon icon={FiChevronLeft} />
            </button>
            <button
              onClick={nextMonth}
              className="p-1.5 hover:bg-brand-light rounded-full text-text-muted transition-colors"
            >
              <SafeIcon icon={FiChevronRight} />
            </button>
          </div>
        </div>

        {/* Day-of-week headers */}
        <div className="grid grid-cols-7 gap-1 mb-4">
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
            <div
              key={`header-${i}`}
              className="text-center text-[9px] font-black text-text-muted uppercase py-1"
            >
              {day}
            </div>
          ))}

          {/* Empty cells before the 1st */}
          {Array.from({ length: firstDayOfMonth }).map((_, i) => (
            <div key={`empty-${i}`} className="aspect-square" />
          ))}

          {/* Day cells */}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day     = i + 1;
            const webinar = webinarDates[day];
            const isToday =
              new Date().getDate()    === day &&
              new Date().getMonth()   === month &&
              new Date().getFullYear() === year;

            return (
              <button
                key={`day-${day}`}
                onClick={() => webinar && setSelectedWebinar(webinar)}
                disabled={!webinar}
                className={`relative aspect-square flex items-center justify-center text-[10px] rounded-lg transition-all ${
                  webinar
                    ? 'bg-accent-gold text-white font-bold shadow-lg shadow-accent-gold/20 scale-105 z-10 cursor-pointer'
                    : isToday
                    ? 'bg-accent-navy text-white font-bold shadow-md cursor-default'
                    : 'text-text-muted hover:bg-brand-light cursor-default'
                }`}
              >
                {day}
                {webinar && (
                  <div className="absolute bottom-0.5 w-1 h-1 bg-white rounded-full" />
                )}
              </button>
            );
          })}
        </div>

        {/* Upcoming list */}
        <div className="pt-4 border-t border-brand-light">
          <p className="text-[9px] font-black uppercase tracking-widest text-text-muted mb-4">
            Upcoming This Month
          </p>
          <div className="space-y-3 max-h-[240px] overflow-y-auto pr-1 custom-scrollbar">
            {Object.keys(webinarDates).length > 0 ? (
              Object.entries(webinarDates)
                .sort(([a], [b]) => Number(a) - Number(b))
                .map(([day, w]) => (
                  <button
                    key={w.id ?? day}
                    onClick={() => setSelectedWebinar(w)}
                    className={`w-full text-left group transition-all p-2.5 rounded-2xl border ${
                      selectedWebinar?.id === w.id
                        ? 'bg-accent-navy border-accent-navy'
                        : 'bg-brand-light/50 border-transparent hover:border-accent-gold'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 font-black text-[10px] ${
                          selectedWebinar?.id === w.id
                            ? 'bg-white/10 text-accent-gold'
                            : 'bg-white text-accent-gold shadow-sm'
                        }`}
                      >
                        {day}
                      </div>
                      <div className="flex-grow min-w-0">
                        <p
                          className={`text-[11px] font-bold truncate ${
                            selectedWebinar?.id === w.id
                              ? 'text-white'
                              : 'text-accent-navy group-hover:text-accent-gold'
                          }`}
                        >
                          {w.title || 'Untitled'}
                        </p>
                        <p
                          className={`text-[9px] mt-0.5 ${
                            selectedWebinar?.id === w.id ? 'text-white/60' : 'text-text-muted'
                          }`}
                        >
                          {w.time || '—'} • {speakerFirst(w.speaker)}
                        </p>
                      </div>
                    </div>
                  </button>
                ))
            ) : (
              <p className="text-[10px] text-text-muted italic py-4 text-center">
                No sessions this month.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Host CTA */}
      <div className="bg-white p-8 rounded-[32px] border border-brand-dark text-center group">
        <div className="w-16 h-16 bg-accent-gold/10 text-accent-gold rounded-3xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform border border-accent-gold/20 shadow-sm">
          <SafeIcon icon={FiUsers} className="w-8 h-8" />
        </div>
        <h5 className="font-serif text-xl text-accent-navy mb-3">Host a Session</h5>
        <p className="text-sm text-text-muted font-light mb-8 italic">
          "Share your expertise with our compassionate community."
        </p>
        <button
          onClick={() => setIsProposalOpen(true)}
          className="w-full btn-secondary py-4 rounded-2xl shadow-lg active:scale-95"
        >
          Become a Speaker
        </button>
      </div>

      {/* Selected webinar detail card */}
      <AnimatePresence mode="wait">
        {selectedWebinar && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="bg-accent-navy text-white p-6 rounded-[32px] shadow-xl relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-24 h-24 bg-accent-gold/10 rounded-full -mr-12 -mt-12 blur-2xl" />
            <button
              onClick={() => setSelectedWebinar(null)}
              className="absolute top-4 right-4 text-white/40 hover:text-white"
            >
              <SafeIcon icon={FiX} />
            </button>
            <h5 className="text-accent-gold text-[9px] font-black uppercase tracking-widest mb-2">
              Selected Session
            </h5>
            <h4 className="text-lg font-serif mb-4 leading-tight">
              {selectedWebinar.title || 'Untitled'}
            </h4>
            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-2 text-[10px] text-white/70">
                <SafeIcon icon={FiCalendar} className="text-accent-gold" />
                <span>{selectedWebinar.date || '—'}</span>
              </div>
              <div className="flex items-center gap-2 text-[10px] text-white/70">
                <SafeIcon icon={FiClock} className="text-accent-gold" />
                <span>
                  {selectedWebinar.time || '—'}
                  {selectedWebinar.duration ? ` (${selectedWebinar.duration})` : ''}
                </span>
              </div>
            </div>

            {registeredIds.includes(selectedWebinar.id) ? (
              <button
                onClick={() => navigate(`/webinar/${selectedWebinar.id}`)}
                className="w-full bg-accent-gold text-white py-3 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-accent-gold/90 transition-all shadow-lg shadow-accent-gold/20"
              >
                <SafeIcon icon={FiZap} /> Enter Lobby
              </button>
            ) : (
              <button
                onClick={() => navigate('/webinars')}
                className="w-full bg-white/10 hover:bg-white/20 border border-white/20 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all"
              >
                View Registration
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <SpeakerProposalModal
        isOpen={isProposalOpen}
        onClose={() => setIsProposalOpen(false)}
      />
    </div>
  );
};

export default WebinarSidebarCalendar;